import React, { useState } from 'react';
import {
  Box,
  Button,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useCart } from '../contexts/CartContext';

interface CheckoutFormProps {
  shippingInfo: {
    name: string;
    email: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  cartItems: Array<{
    card: any;
    quantity: number;
  }>;
  onPaymentSuccess: (orderConfirmation: any) => void;
  onPaymentError: (error: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  validateForm: () => boolean;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  shippingInfo,
  cartItems,
  onPaymentSuccess,
  onPaymentError,
  isProcessing,
  setIsProcessing,
  validateForm,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { clearCart } = useCart();
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success`,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed');
      }

      if (paymentIntent?.status === 'succeeded') {
        // Clear the cart and show success
        clearCart();
        onPaymentSuccess({
          paymentIntentId: paymentIntent.id,
          status: 'succeeded',
          items: cartItems,
          total: cartItems.reduce((sum, item) => sum + (item.card.price * item.quantity), 0),
          customerEmail: shippingInfo.email,
          shippingAddress: shippingInfo,
        });
      } else {
        throw new Error('Payment was not successful');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setError(errorMessage);
      onPaymentError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };


  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || isProcessing}
        sx={{ py: 1.5 }}
      >
        {isProcessing ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            Processing Payment...
          </Box>
        ) : (
          `Pay $${cartItems.reduce((sum, item) => sum + (item.card.price * item.quantity), 0).toFixed(2)}`
        )}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        Your payment information is secure and encrypted
      </Typography>
    </Box>
  );
};

export default CheckoutForm;
